import Header from "@/components/header";
import { NoResult } from "@/components/noResult";
import { useCustomTable } from "@/hooks/use-custom-table";
import useIsSmall from "@/hooks/useIsSmall";
import { CustomerPurchaseDto, PurchasePaymentMethod, PurchasePaymentStatus } from "@/models/club";
import useGetInvoices from "@/services/customer/hooks/useGetInvoices";
import useGetInvoicesInfinite from "@/services/customer/hooks/useGetInvoicesInfinite";
import { useResendPaymentLinkSms, useRetryPurchase } from "@/services/payment/utils";
import { ActionIcon, Alert, Badge, Button, Card, Center, Container, Divider, Group, Loader, Menu, Modal, Skeleton, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconCash, IconCheck, IconClock, IconDotsVertical, IconEye, IconFileInvoice, IconInfoCircle, IconMessageForward, IconRefresh, IconRosetteDiscountCheckFilled, IconX } from "@tabler/icons-react";
import { MantineReactTable, MRT_ColumnDef, MRT_PaginationState } from "mantine-react-table";
import { MRT_Localization_FA } from "mantine-react-table/locales/fa/index.cjs";
import { useCallback, useMemo, useRef, useState } from "react";
import InvoiceDetailsModal from "./InvoiceDetailsModal";
import { InvoiceDepositSummary } from "./DepositGatewayBadge";
import classes from "./invoices.module.scss";

const money = (value?: number | null) => typeof value === "number" ? value.toLocaleString("fa-IR") : "ـ";

const methodLabel = (method?: PurchasePaymentMethod) => {
  if (method === PurchasePaymentMethod.Value1) return "اسنپ‌پی";
  if (method === PurchasePaymentMethod.Value2) return "ترکیبی";
  return "پرداخت مستقیم";
};

const canRetryPurchase = (status?: PurchasePaymentStatus) =>
  status === PurchasePaymentStatus.Value3;

function PaymentStatusBadge({ status }: { status?: PurchasePaymentStatus }) {
  if (status === PurchasePaymentStatus.Value1) return <Badge size="lg" color="green" variant="light" leftSection={<IconCheck size={14} />}>پرداخت شده</Badge>;
  if (status === PurchasePaymentStatus.Value3) return <Badge size="lg" color="red" variant="light" leftSection={<IconX size={14} />}>ناموفق</Badge>;
  return <Badge size="lg" color="yellow" variant="light" leftSection={<IconClock size={14} />}>در انتظار پرداخت</Badge>;
}

type RetryConfirmationModalProps = {
  purchaseId?: number;
  opened: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (purchaseId: number) => void;
};

function RetryConfirmationModal({
  purchaseId,
  opened,
  loading,
  onClose,
  onConfirm,
}: RetryConfirmationModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={loading ? () => undefined : onClose}
      title={<Text fw={800}>تلاش مجدد پرداخت</Text>}
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      withCloseButton={!loading}
    >
      <Stack gap="md">
        <Alert color="orange" variant="light" icon={<IconInfoCircle size={20} />}>
          با ادامه این عملیات، یک فاکتور و کد سفارش جدید ساخته می‌شود و لینک پرداخت تازه برای مشتری پیامک خواهد شد. فاکتور ناموفق فعلی نیز برای سوابق باقی می‌ماند.
        </Alert>
        <Text size="sm">
          آیا از تلاش مجدد برای فاکتور #{purchaseId} مطمئن هستید؟
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={loading}>
            انصراف
          </Button>
          <Button
            color="orange"
            leftSection={<IconRefresh size={17} />}
            loading={loading}
            disabled={!purchaseId}
            onClick={() => purchaseId && onConfirm(purchaseId)}
          >
            تلاش مجدد
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DesktopInvoices() {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>();
  const [retryInvoiceId, setRetryInvoiceId] = useState<number>();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { data, isLoading, isFetching, refetch } = useGetInvoices({
    params: {
      Count: pagination.pageSize,
      Skip: pagination.pageIndex * pagination.pageSize,
    },
  });
  const invoices = data?.result || [];
  const rowCount = data?.count ?? (
    pagination.pageIndex * pagination.pageSize +
    invoices.length +
    (invoices.length === pagination.pageSize ? 1 : 0)
  );
  const { mutate: resendSms, isLoading: isResending, variables: resendingId } = useResendPaymentLinkSms();
  const { mutate: retryPurchase, isLoading: isRetrying, variables: retryingId } = useRetryPurchase();

  const confirmRetry = (purchaseId: number) => {
    retryPurchase(purchaseId, {
      onSuccess: () => setRetryInvoiceId(undefined),
    });
  };

  const columns = useMemo<MRT_ColumnDef<CustomerPurchaseDto>[]>(() => [
    {
      header: "شماره فاکتور", accessorKey: "id", size: 100,
      Cell: ({ row }) => <Group gap={6} wrap="nowrap"><ThemeIcon size="sm" variant="light" color="violet"><IconFileInvoice size={14} /></ThemeIcon><Text fw={700}>#{row.original.id}</Text></Group>,
    },
    {
      header: "مشتری", id: "customer", size: 160,
      accessorFn: (row) => `${row.customer?.firstName || ""} ${row.customer?.lastName || ""}`.trim() || "ـ",
      Cell: ({ row }) => <Stack gap={1} align="center"><Text size="sm" fw={600} ta="center">{`${row.original.customer?.firstName || ""} ${row.original.customer?.lastName || ""}`.trim() || "ـ"}</Text><Text size="xs" c="dimmed" ta="center">{row.original.customer?.phoneNumber || "شماره ثبت نشده"}</Text></Stack>,
    },
    {
      header: "مبلغ کل (ریال)", accessorKey: "rateBreakdown.amount", size: 130,
      Cell: ({ row }) => <Text fw={800}>{money(row.original.rateBreakdown?.amount)}</Text>,
    },
    {
      header: "پیش‌پرداخت رزرو", id: "deposits", size: 210,
      accessorFn: (row) => row.deposits?.reduce((sum, deposit) => sum + (deposit.amount || 0), 0) || 0,
      Cell: ({ row }) => <InvoiceDepositSummary deposits={row.original.deposits} />,
    },
    {
      header: "روش پرداخت", accessorKey: "paymentMethod", size: 125,
      Cell: ({ row }) => {
        const isSnapp = row.original.paymentMethod === PurchasePaymentMethod.Value1 || row.original.paymentMethod === PurchasePaymentMethod.Value2 || (row.original.creditAmount || 0) > 0;
        return <Badge color={isSnapp ? "violet" : "blue"} variant={isSnapp ? "filled" : "light"} leftSection={isSnapp ? <IconRosetteDiscountCheckFilled size={14} /> : <IconCash size={14} />}>{methodLabel(row.original.paymentMethod)}</Badge>;
      },
    },
    { header: "تخفیف", accessorKey: "discountAmount", size: 110, Cell: ({ row }) => { const total = (row.original.discountAmount || 0) + (row.original.appointmentDiscountAmount || 0); return total > 0 ? <Text size="xs" c="green.8" title={row.original.discountCode || ""}>−{money(total)}</Text> : "ـ"; } },
    { header: "دریافت حضوری", accessorKey: "directAmount", size: 110, Cell: ({ row }) => money(row.original.directAmount) },
    { header: "وضعیت پرداخت", accessorKey: "paymentStatus", size: 155, Cell: ({ row }) => <PaymentStatusBadge status={row.original.paymentStatus} /> },
    { header: "توضیحات", accessorKey: "description", size: 160, Cell: ({ row }) => <Text size="xs" lineClamp={1} title={row.original.description || ""}>{row.original.description || "ـ"}</Text> },
  ], []);

  const table = useCustomTable({
    columns,
    data: invoices,
    localization: MRT_Localization_FA,
    enableGlobalFilter: false,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableRowActions: true,
    enablePagination: true,
    manualPagination: true,
    rowCount,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    layoutMode: "grid-no-grow",
    initialState: { density: "xs" },
    state: { isLoading: isLoading || isFetching, pagination },
    displayColumnDefOptions: {
      "mrt-row-actions": { size: 48, grow: false },
    },
    mantineTableHeadCellProps: {
      className: classes.centeredHeader,
      style: { padding: "8px 10px", minHeight: 42, fontSize: 11, textAlign: "center" },
    },
    mantineTableBodyCellProps: {
      className: classes.centeredCell,
      style: { padding: "8px 10px", minHeight: 48, textAlign: "center" },
    },
    mantineBottomToolbarProps: {
      style: { minHeight: 48 },
    },
    mantineTableBodyRowProps: ({ row }) => ({ style: { borderRight: row.original.paymentStatus === PurchasePaymentStatus.Value1 ? "4px solid var(--mantine-color-green-6)" : "4px solid var(--mantine-color-yellow-6)" } }),
    renderRowActions: ({ row }) => (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" aria-label="عملیات فاکتور">
            <IconDotsVertical size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconEye size={16} />}
            disabled={!row.original.id}
            onClick={() => setSelectedInvoiceId(row.original.id)}
          >
            مشاهده جزئیات فاکتور
          </Menu.Item>
          <Menu.Item
            color="orange"
            leftSection={<IconRefresh size={16} />}
            disabled={!canRetryPurchase(row.original.paymentStatus) || !row.original.id || isRetrying}
            onClick={() => setRetryInvoiceId(row.original.id)}
          >
            {isRetrying && retryingId === row.original.id ? "در حال تلاش مجدد..." : "تلاش مجدد پرداخت"}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconMessageForward size={16} />}
            disabled={
              !row.original.transactionId ||
              !row.original.id ||
              row.original.paymentStatus !== PurchasePaymentStatus.Value0
            }
            onClick={() => row.original.id && resendSms(row.original.id)}
          >
            {isResending && resendingId === row.original.id
              ? "در حال ارسال..."
              : "ارسال مجدد پیامک پرداخت"}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ),
  });

  return (
    <Container size="full" px="sm" py="xs">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" mb="xs">
          <Stack gap={0}>
            <Title order={4}>فاکتورها</Title>
            <Text size="sm" pt="xs">
              فاکتورهای ثبت‌شده و وضعیت پرداخت مستقیم یا اسنپ‌پی را مشاهده و مدیریت کنید.
            </Text>
          </Stack>
          <Button
            variant="default"
            leftSection={<IconRefresh size={17} />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            تازه‌سازی
          </Button>
        </Group>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={20} />}>
          در صورتی که پیامک برای مشتری ارسال نشد، با استفاده از کد سفارش نسبت به پرداخت دستی اقدام نمایید.
        </Alert>
        <MantineReactTable table={table} />
      </Stack>
      <InvoiceDetailsModal
        purchaseId={selectedInvoiceId}
        opened={typeof selectedInvoiceId === "number"}
        onClose={() => setSelectedInvoiceId(undefined)}
      />
      <RetryConfirmationModal
        purchaseId={retryInvoiceId}
        opened={typeof retryInvoiceId === "number"}
        loading={isRetrying}
        onClose={() => setRetryInvoiceId(undefined)}
        onConfirm={confirmRetry}
      />
    </Container>
  );
}

function InvoiceCard({ invoice, onResend, onRetry, onDetails, isResending, isRetrying }: { invoice: CustomerPurchaseDto; onResend: (id: number) => void; onRetry: (id: number) => void; onDetails: (id: number) => void; isResending: boolean; isRetrying: boolean }) {
  const isSnapp =
    invoice.paymentMethod === PurchasePaymentMethod.Value1 ||
    invoice.paymentMethod === PurchasePaymentMethod.Value2 ||
    (invoice.creditAmount || 0) > 0;
  const customerName = `${invoice.customer?.firstName || ""} ${invoice.customer?.lastName || ""}`.trim() || "مشتری بدون نام";

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="light" color="violet" radius="md">
              <IconFileInvoice size={18} />
            </ThemeIcon>
            <Stack gap={1}>
              <Text fw={800}>فاکتور #{invoice.id}</Text>
              <Text size="xs" c="dimmed">{customerName}</Text>
            </Stack>
          </Group>
          <Group gap={4} wrap="nowrap">
            <PaymentStatusBadge status={invoice.paymentStatus} />
            <Menu position="bottom-end" withinPortal>
              <Menu.Target><ActionIcon variant="subtle" aria-label="عملیات فاکتور"><IconDotsVertical size={18} /></ActionIcon></Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconEye size={16} />}
                  disabled={!invoice.id}
                  onClick={() => invoice.id && onDetails(invoice.id)}
                >
                  مشاهده جزئیات فاکتور
                </Menu.Item>
                <Menu.Item
                  color="orange"
                  leftSection={<IconRefresh size={16} />}
                  disabled={!canRetryPurchase(invoice.paymentStatus) || !invoice.id || isRetrying}
                  onClick={() => invoice.id && onRetry(invoice.id)}
                >
                  {isRetrying ? "در حال تلاش مجدد..." : "تلاش مجدد پرداخت"}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconMessageForward size={16} />}
                  disabled={
                    !invoice.transactionId ||
                    !invoice.id ||
                    invoice.paymentStatus !== PurchasePaymentStatus.Value0
                  }
                  onClick={() => invoice.id && onResend(invoice.id)}
                >
                  {isResending ? "در حال ارسال..." : "ارسال مجدد پیامک پرداخت"}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Text size="sm" c="dimmed">مبلغ کل</Text>
          <Text fw={900}>{money(invoice.rateBreakdown?.amount)} <Text span size="xs">ریال</Text></Text>
        </Group>

        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="sm" c="dimmed">پیش‌پرداخت رزرو</Text>
          <InvoiceDepositSummary deposits={invoice.deposits} />
        </Group>

        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">کد سفارش</Text>
          {invoice.transactionId
            ? <Text ff="monospace" size="sm" fw={700}>{invoice.transactionId}</Text>
            : <Text size="xs" c="dimmed">پرداخت نقدی / مستقیم</Text>}
        </Group>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">روش پرداخت</Text>
          <Badge
            color={isSnapp ? "violet" : "blue"}
            variant={isSnapp ? "filled" : "light"}
            leftSection={isSnapp ? <IconRosetteDiscountCheckFilled size={14} /> : <IconCash size={14} />}
          >
            {methodLabel(invoice.paymentMethod)}
          </Badge>
        </Group>

        {(invoice.discountAmount || 0) > 0 && (
          <Text size="xs" c="green.8">تخفیف{invoice.discountCode ? ` ${invoice.discountCode}` : ""}: −{money(invoice.discountAmount)} ریال</Text>
        )}
        {(invoice.appointmentDiscountAmount || 0) > 0 && (
          <Text size="xs" c="green.8">تخفیف رزرو آنلاین: −{money(invoice.appointmentDiscountAmount)} ریال</Text>
        )}
        {isSnapp && (
          <Group grow>
            <Stack gap={2} p="xs" bg="gray.0" style={{ borderRadius: 8 }}>
              <Text size="xs" c="dimmed">دریافت حضوری</Text>
              <Text size="sm" fw={700}>{money(invoice.directAmount)} ریال</Text>
            </Stack>
            <Stack gap={2} p="xs" bg="violet.0" style={{ borderRadius: 8 }}>
              <Text size="xs" c="violet.7">سهم اسنپ‌پی</Text>
              <Text size="sm" fw={800} c="violet.8">{money(invoice.creditAmount)} ریال</Text>
            </Stack>
          </Group>
        )}

        {invoice.description && (
          <Text size="xs" c="dimmed" lineClamp={2}>{invoice.description}</Text>
        )}
      </Stack>
    </Card>
  );
}

function MobileInvoices() {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>();
  const [retryInvoiceId, setRetryInvoiceId] = useState<number>();
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useGetInvoicesInfinite();
  const observer = useRef<IntersectionObserver | null>(null);
  const { mutate: resendSms, isLoading: isResending, variables: resendingId } = useResendPaymentLinkSms();
  const { mutate: retryPurchase, isLoading: isRetrying, variables: retryingId } = useRetryPurchase();

  const confirmRetry = (purchaseId: number) => {
    retryPurchase(purchaseId, {
      onSuccess: () => setRetryInvoiceId(undefined),
    });
  };

  const lastInvoiceRef = useCallback((node: HTMLDivElement | null) => {
    if (isFetchingNextPage) return;
    observer.current?.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage) fetchNextPage();
    });
    if (node) observer.current.observe(node);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const invoices = data?.pages.flatMap((page) => page.result) || [];

  return (
    <>
      <Header
        title="فاکتورها"
        showBack
        route="/dashboard"
        customComponent={
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={15} />}
            loading={isFetching && !isFetchingNextPage}
            onClick={() => refetch()}
          >
            تازه‌سازی
          </Button>
        }
      />
      <Container w="100%" size="sm" p="md">
        <Stack gap={0} mb="md">
          <Title order={4}>فاکتورها</Title>
          <Text size="sm" pt="xs">
            فاکتورها و وضعیت پرداخت مشتریان را مشاهده و مدیریت کنید.
          </Text>
        </Stack>
        <Alert mb="md" color="blue" variant="light" icon={<IconInfoCircle size={20} />}>
          اگر پیامک به مشتری نرسید، از کد سفارش برای پرداخت دستی استفاده کنید.
        </Alert>
        {isLoading ? (
          <Stack>
            {[1, 2, 3].map((item) => <Skeleton key={item} h={190} radius="md" />)}
          </Stack>
        ) : invoices.length === 0 ? (
          <NoResult />
        ) : (
          <Stack gap="sm">
            {invoices.map((invoice, index) => (
              <div
                key={invoice.id ?? index}
                ref={index === invoices.length - 1 ? lastInvoiceRef : undefined}
              >
                <InvoiceCard
                  invoice={invoice}
                  onResend={resendSms}
                  onRetry={setRetryInvoiceId}
                  onDetails={setSelectedInvoiceId}
                  isResending={isResending && resendingId === invoice.id}
                  isRetrying={isRetrying && retryingId === invoice.id}
                />
              </div>
            ))}
            {isFetchingNextPage && (
              <Center py="md"><Loader type="dots" /></Center>
            )}
          </Stack>
        )}
      </Container>
      <InvoiceDetailsModal
        purchaseId={selectedInvoiceId}
        opened={typeof selectedInvoiceId === "number"}
        onClose={() => setSelectedInvoiceId(undefined)}
      />
      <RetryConfirmationModal
        purchaseId={retryInvoiceId}
        opened={typeof retryInvoiceId === "number"}
        loading={isRetrying}
        onClose={() => setRetryInvoiceId(undefined)}
        onConfirm={confirmRetry}
      />
    </>
  );
}

function Invoices() {
  const isSmall = useIsSmall();
  return isSmall ? <MobileInvoices /> : <DesktopInvoices />;
}

export default Invoices;
